/**
 * DebtCaseAdvancedPanels — Phase 2 + Phase 3 admin panels for a debt case:
 *   • MandatePanel  — draft a Debt Mandate e-sign (waives esign fee)
 *   • BureauPanel   — consent capture + sandbox bureau pull + G/A/R flags
 *   • CamLitePanel  — generate + view CAM-Lite credit snapshots
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button, Pill } from "../common/Primitives";
import api, { formatApiError } from "../../lib/api";
import { formatDateTime } from "../../lib/datetime";
import { FileSignature, ShieldCheck, ClipboardCheck, ChevronDown, ExternalLink } from "lucide-react";

/* ------------------------------------------------------------------ */
/* Mandate                                                             */
/* ------------------------------------------------------------------ */
export function MandatePanel({ caseId, borrowerEmail, borrowerName }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    borrower_signer_email: borrowerEmail || "",
    borrower_signer_name: borrowerName || "",
    venturaz_signer_email: "",
    venturaz_signer_name: "",
    scope: "Engagement as advisor for the debt raise contemplated under this case.",
    upfront_fee_inr: 0,
    success_fee_pct: 1.0,
    minimum_fee_inr: 0,
    validity_days: 90,
    exclusivity: false,
    notes: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/debt/cases/${caseId}/mandates`);
      setRows(r.data?.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [caseId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    setForm((f) => ({ ...f, borrower_signer_email: f.borrower_signer_email || borrowerEmail || "", borrower_signer_name: f.borrower_signer_name || borrowerName || "" }));
  }, [borrowerEmail, borrowerName]);

  const save = async () => {
    if (!form.borrower_signer_email || !form.venturaz_signer_email || !form.scope) {
      toast.error("Fill borrower + VenturaZ signers and scope.");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/debt/cases/${caseId}/mandate`, {
        ...form,
        upfront_fee_inr: Number(form.upfront_fee_inr) || 0,
        success_fee_pct: Number(form.success_fee_pct) || 0,
        minimum_fee_inr: Number(form.minimum_fee_inr) || 0,
        validity_days: Number(form.validity_days) || 90,
      });
      toast.success("Mandate drafted — no e-sign charge (debt mandate waiver).");
      setOpen(false); await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="border border-navy/10 rounded bg-white/60 p-4" data-testid="debt-mandate-panel">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="overline flex items-center gap-1"><FileSignature size={11} /> Mandate (e-sign · free for debt)</div>
          <div className="text-[10px] font-mono text-ink-muted">Drafts a Debt Mandate envelope with the wallet-debit skipped.</div>
        </div>
        {!open && <button onClick={() => setOpen(true)} className="text-[11px] font-heading text-navy hover:bg-navy/10 rounded px-2 py-1 border border-navy/30" data-testid="mandate-new">Draft mandate</button>}
      </div>
      {open && (
        <div className="border border-navy/15 rounded p-3 mb-3 bg-white/70 space-y-2" data-testid="mandate-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <Field label="Borrower signer name" v={form.borrower_signer_name} on={(x) => setForm({ ...form, borrower_signer_name: x })} testId="mandate-borrower-name" />
            <Field label="Borrower signer email" v={form.borrower_signer_email} on={(x) => setForm({ ...form, borrower_signer_email: x })} testId="mandate-borrower-email" />
            <Field label="VenturaZ signer name" v={form.venturaz_signer_name} on={(x) => setForm({ ...form, venturaz_signer_name: x })} testId="mandate-vz-name" />
            <Field label="VenturaZ signer email" v={form.venturaz_signer_email} on={(x) => setForm({ ...form, venturaz_signer_email: x })} testId="mandate-vz-email" />
            <Field label="Upfront fee (₹)" type="number" v={form.upfront_fee_inr} on={(x) => setForm({ ...form, upfront_fee_inr: x })} testId="mandate-upfront" />
            <Field label="Success fee %" type="number" v={form.success_fee_pct} on={(x) => setForm({ ...form, success_fee_pct: x })} testId="mandate-success" />
            <Field label="Minimum fee (₹)" type="number" v={form.minimum_fee_inr} on={(x) => setForm({ ...form, minimum_fee_inr: x })} testId="mandate-minimum" />
            <Field label="Validity (days)" type="number" v={form.validity_days} on={(x) => setForm({ ...form, validity_days: x })} testId="mandate-validity" />
          </div>
          <div>
            <label className="text-[10px] font-mono text-ink-muted">Scope</label>
            <textarea rows={2} value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })}
              className="w-full px-2 py-1.5 border border-navy/15 rounded text-xs text-ink bg-white" data-testid="mandate-scope" />
          </div>
          <label className="text-xs inline-flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.exclusivity} onChange={(e) => setForm({ ...form, exclusivity: e.target.checked })} data-testid="mandate-exclusivity" /> Exclusivity
          </label>
          <Button variant="gold" onClick={save} disabled={saving} testId="mandate-save">{saving ? "Drafting…" : "Draft mandate (no e-sign charge)"}</Button>
        </div>
      )}
      {loading ? <div className="text-xs italic text-ink-muted">Loading…</div>
        : rows.length === 0 ? <div className="text-xs italic text-ink-muted">No mandates drafted yet.</div>
        : (
          <div className="space-y-1">
            {rows.map((m) => (
              <div key={m.mandate_id} className="border border-navy/[0.06] rounded p-2 text-xs" data-testid={`mandate-${m.mandate_id}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="font-heading text-ink">{m.scope?.slice(0, 80)}{m.scope?.length > 80 ? "…" : ""}</div>
                    <div className="text-[10px] font-mono text-ink-muted">
                      Success fee {m.success_fee_pct}% · Upfront ₹{Number(m.upfront_fee_inr || 0).toLocaleString("en-IN")} · Validity {m.validity_days}d
                    </div>
                  </div>
                  <Pill tone={m.envelope?.status === "completed" ? "success" : m.envelope?.status === "sent" ? "gold" : "muted"}>
                    {m.envelope?.status || m.status}
                  </Pill>
                </div>
                {m.envelope?.esign_charge_source === "debt_mandate_waiver" && (
                  <div className="mt-1 text-[10px] font-mono text-emerald-800">✓ E-sign charge waived (debt mandate)</div>
                )}
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Bureau                                                              */
/* ------------------------------------------------------------------ */
export function BureauPanel({ caseId, borrowerName, borrowerPan }) {
  const [rows, setRows] = useState({ reports: [], snapshots: [] });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    borrower_name: borrowerName || "",
    borrower_pan: borrowerPan || "",
    consent_text: "I authorise VenturaZ to obtain my credit information report from the credit bureau for the purpose of evaluating my loan application. I understand this constitutes a soft/hard enquiry as applicable per RBI norms.",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/debt/cases/${caseId}/bureau`);
      setRows(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [caseId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setForm((f) => ({ ...f, borrower_name: f.borrower_name || borrowerName || "" })); }, [borrowerName]);

  const pull = async () => {
    if (!form.borrower_pan || form.borrower_pan.length !== 10) { toast.error("Valid 10-char PAN required"); return; }
    setSaving(true);
    try {
      await api.post(`/debt/cases/${caseId}/bureau/pull?provider=sandbox`, form);
      toast.success("Bureau report pulled (sandbox provider — not a live bureau).");
      setOpen(false); await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const latest = rows.snapshots?.[0];
  return (
    <div className="border border-navy/10 rounded bg-white/60 p-4" data-testid="debt-bureau-panel">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="overline flex items-center gap-1"><ShieldCheck size={11} /> Credit Bureau (Sandbox)</div>
          <div className="text-[10px] font-mono text-ink-muted">Consent-first pull. Live CIBIL / Experian / CRIF / Equifax adapters slot in when credentials are configured.</div>
        </div>
        {!open && <button onClick={() => setOpen(true)} className="text-[11px] font-heading text-navy hover:bg-navy/10 rounded px-2 py-1 border border-navy/30" data-testid="bureau-new">Capture consent & pull</button>}
      </div>
      {open && (
        <div className="border border-navy/15 rounded p-3 mb-3 bg-white/70 space-y-2" data-testid="bureau-form">
          <Field label="Borrower name" v={form.borrower_name} on={(x) => setForm({ ...form, borrower_name: x })} testId="bureau-name" />
          <Field label="Borrower PAN (10 chars)" v={form.borrower_pan} on={(x) => setForm({ ...form, borrower_pan: (x || "").toUpperCase() })} testId="bureau-pan" />
          <div>
            <label className="text-[10px] font-mono text-ink-muted">Consent text (as read / agreed by borrower)</label>
            <textarea rows={3} value={form.consent_text} onChange={(e) => setForm({ ...form, consent_text: e.target.value })}
              className="w-full px-2 py-1.5 border border-navy/15 rounded text-xs text-ink bg-white" data-testid="bureau-consent" />
          </div>
          <Button variant="gold" onClick={pull} disabled={saving} testId="bureau-pull">{saving ? "Pulling…" : "Confirm consent & pull (sandbox)"}</Button>
          <div className="text-[10px] font-mono text-amber-800">
            Sandbox provider only — never fabricates real bureau data. Configure a live provider to switch.
          </div>
        </div>
      )}
      {loading ? <div className="text-xs italic text-ink-muted">Loading…</div>
        : (rows.snapshots || []).length === 0 ? <div className="text-xs italic text-ink-muted">No bureau report yet.</div>
        : (
          <div>
            {latest && (
              <div className="mb-2">
                <div className="text-[10px] font-mono text-ink-muted">Latest snapshot · {formatDateTime(latest.generated_at)}</div>
                <div className="mt-1 flex flex-wrap items-center gap-3">
                  <div>
                    <div className="font-display text-3xl text-ink">{latest.score}</div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">CIBIL score</div>
                  </div>
                  <div className="flex flex-wrap gap-1 max-w-xl">
                    {(latest.flags || []).map((f, i) => (
                      <span key={i} className={`text-[10px] font-mono px-2 py-0.5 rounded ${
                        f.level === "green" ? "bg-emerald-500/10 text-emerald-800"
                        : f.level === "amber" ? "bg-amber-500/10 text-amber-800"
                        : "bg-rose-500/10 text-rose-800"
                      }`} data-testid={`bureau-flag-${f.field}`}>
                        {f.field}: {f.note}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
            <details className="mt-2">
              <summary className="cursor-pointer text-[11px] text-navy font-heading">Historic pulls ({(rows.reports || []).length})</summary>
              <ul className="mt-1 space-y-1">
                {(rows.reports || []).map((r) => (
                  <li key={r.report_id} className="text-[11px] font-mono text-ink-muted">
                    {formatDateTime(r.pulled_at)} · {r.provider} · {r.is_live ? "LIVE" : "sandbox"}
                  </li>
                ))}
              </ul>
            </details>
          </div>
        )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* CAM-Lite                                                            */
/* ------------------------------------------------------------------ */
export function CamLitePanel({ caseId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/debt/cases/${caseId}/cam-lite`);
      setRows(r.data?.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [caseId]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true);
    try {
      await api.post(`/debt/cases/${caseId}/cam-lite`);
      toast.success("CAM-Lite snapshot generated");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-navy/10 rounded bg-white/60 p-4" data-testid="debt-cam-panel">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="overline flex items-center gap-1"><ClipboardCheck size={11} /> CAM-Lite (Credit Analyst Memo)</div>
          <div className="text-[10px] font-mono text-ink-muted">Snapshot built from latest PD + bureau + financials. Green / Amber / Red flags.</div>
        </div>
        <button onClick={generate} disabled={busy} className="text-[11px] font-heading text-navy hover:bg-navy/10 rounded px-2 py-1 border border-navy/30" data-testid="cam-generate">
          {busy ? "Generating…" : "Generate CAM-Lite"}
        </button>
      </div>
      {loading ? <div className="text-xs italic text-ink-muted">Loading…</div>
        : rows.length === 0 ? <div className="text-xs italic text-ink-muted">No CAM snapshots yet.</div>
        : (
          <div className="space-y-2">
            {rows.map((c) => {
              const on = !!expanded[c.cam_id];
              const green = (c.flags || []).filter((f) => f.level === "green").length;
              const amber = (c.flags || []).filter((f) => f.level === "amber").length;
              const red = (c.flags || []).filter((f) => f.level === "red").length;
              return (
                <div key={c.cam_id} className="border border-navy/[0.06] rounded" data-testid={`cam-${c.cam_id}`}>
                  <button onClick={() => setExpanded((e) => ({ ...e, [c.cam_id]: !on }))}
                    className="w-full flex items-center justify-between px-3 py-2 text-left" data-testid={`cam-toggle-${c.cam_id}`}>
                    <div>
                      <div className="font-heading text-ink text-sm">Verdict: {c.verdict}</div>
                      <div className="text-[10px] font-mono text-ink-muted">
                        {formatDateTime(c.generated_at)} · by {c.generated_by} · <span className="text-emerald-700">{green} green</span> · <span className="text-amber-700">{amber} amber</span> · <span className="text-rose-700">{red} red</span>
                      </div>
                    </div>
                    <ChevronDown size={14} className={`text-ink-muted transition-transform ${on ? "rotate-180" : ""}`} />
                  </button>
                  {on && (
                    <div className="border-t border-navy/[0.06] p-3 space-y-2 text-xs">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        <Small label="Amount" v={`₹${Number(c.requirement?.amount_inr || 0).toLocaleString("en-IN")}`} />
                        <Small label="Purpose" v={c.requirement?.purpose} />
                        <Small label="Tenure" v={`${c.requirement?.tenure_months} months`} />
                      </div>
                      <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mt-2 mb-1">Flags</div>
                        <div className="flex flex-wrap gap-1">
                          {(c.flags || []).map((f, i) => (
                            <span key={i} className={`text-[10px] font-mono px-2 py-0.5 rounded ${
                              f.level === "green" ? "bg-emerald-500/10 text-emerald-800"
                              : f.level === "amber" ? "bg-amber-500/10 text-amber-800"
                              : "bg-rose-500/10 text-rose-800"
                            }`}>
                              {f.field}: {f.note}
                            </span>
                          ))}
                        </div>
                      </div>
                      {c.bureau_snapshot && (
                        <div className="text-[10px] font-mono text-ink-muted">Bureau score at time of CAM: {c.bureau_snapshot.score}</div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}

function Field({ label, v, on, type = "text", testId }) {
  return (
    <div>
      <label className="text-[10px] font-mono text-ink-muted">{label}</label>
      <input type={type} value={v || ""} onChange={(e) => on(e.target.value)}
        className="w-full px-2 py-1.5 border border-navy/15 rounded text-xs text-ink bg-white" data-testid={testId} />
    </div>
  );
}
function Small({ label, v }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">{label}</div>
      <div className="font-heading text-sm text-ink">{v || "—"}</div>
    </div>
  );
}
